// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the arithmetic behind the Outlook-style swipe on a phone mail row.
// The row follows the finger across the whole width; past the commit line
// the full action arms (colour floods, icon pops, haptic tick) and letting go
// runs it. Short of the line, a side with a tray rests open on it. No DOM, no
// React, so the feel can be unit tested.

/** Fingers wander; below this many pixels a move is still a tap or a long press. */
export const SWIPE_AXIS_LOCK_PX = 10;
/** Default share of the row width a swipe must cover to arm (Settings can change it). */
export const DEFAULT_COMMIT_RATIO = 0.3;
const COMMIT_MIN_PX = 72;
const COMMIT_MAX_PX = 180;
/** Once armed, the finger may drift back this far before the action disarms. */
const DISARM_SLACK_PX = 14;
/** A flick this fast (px/ms) past FLICK_MIN_PX commits even short of the line. */
const FLICK_VELOCITY = 0.9;
const FLICK_MIN_PX = 48;
/** A side with no action still gives a little under the finger, then stops. */
const RUBBER_MAX_PX = 28;
const RUBBER_FALLOFF_PX = 80;
/** The icon trails the row's edge by this much, but never closer to the screen edge than ICON_EDGE_PX. */
const ICON_TRAIL_PX = 36;
const ICON_EDGE_PX = 34;
/** Tray geometry: one button per action plus a little air at the screen edge. */
export const TRAY_BUTTON_PX = 72;
const TRAY_PAD_PX = 10;
/** A pull shorter than this springs back even when there is a tray. */
const TRAY_OPEN_MIN_PX = 32;

export type SwipeAxis = 'horizontal' | 'vertical' | null;

export interface SwipeSides {
	/** A rightward swipe has a full action. */
	leading: boolean;
	/** A leftward swipe has a full action. */
	trailing: boolean;
}

/** Width of the tray each way; 0 when that side has none. */
export interface SwipeTrays {
	leading: number;
	trailing: number;
}

export const NO_TRAYS: SwipeTrays = {leading: 0, trailing: 0};

export function trayWidth(count: number): number {
	return count > 0 ? count * TRAY_BUTTON_PX + TRAY_PAD_PX : 0;
}

/** Decide, once the finger has moved far enough, whether the gesture is ours or the scroller's. */
export function resolveSwipeAxis(dx: number, dy: number): SwipeAxis {
	const ax = Math.abs(dx);
	const ay = Math.abs(dy);
	if (ax < SWIPE_AXIS_LOCK_PX && ay < SWIPE_AXIS_LOCK_PX) return null;
	// Favour the scroller a little: a diagonal drag is a scroll.
	return ax > ay * 1.2 ? 'horizontal' : 'vertical';
}

export function commitDistance(width: number, ratio = DEFAULT_COMMIT_RATIO): number {
	return Math.min(COMMIT_MAX_PX, Math.max(COMMIT_MIN_PX, width * ratio));
}

function rubber(distance: number): number {
	return RUBBER_MAX_PX * (1 - Math.exp(-distance / RUBBER_FALLOFF_PX));
}

function limitOneSide(distance: number, width: number, full: boolean, tray: number): number {
	if (full) return Math.min(distance, width);
	if (tray > 0) return distance <= tray ? distance : tray + rubber(distance - tray);
	return rubber(distance);
}

/**
 * Row offset for a finger at `raw` (the resting offset plus the drag): 1:1 up
 * to the full width where there is a full action, up to the tray where there
 * is only a tray, rubbery where there is nothing.
 */
export function dragOffset(raw: number, width: number, sides: SwipeSides, trays: SwipeTrays = NO_TRAYS): number {
	if (raw > 0) return limitOneSide(raw, width, sides.leading, trays.leading);
	if (raw < 0) return -limitOneSide(-raw, width, sides.trailing, trays.trailing);
	return 0;
}

/** Whether the full action is armed, with a little hysteresis so it doesn't flicker on the line. */
export function isArmed(
	offset: number,
	width: number,
	sides: SwipeSides,
	wasArmed: boolean,
	ratio = DEFAULT_COMMIT_RATIO,
): boolean {
	if (offset > 0 ? !sides.leading : offset < 0 ? !sides.trailing : true) return false;
	const line = commitDistance(width, ratio) - (wasArmed ? DISARM_SLACK_PX : 0);
	return Math.abs(offset) >= line;
}

/** Default share of the width a second (long) action arms at, where a row has one (Home). */
export const DEFAULT_LONG_RATIO = 0.65;

/** How far along a swipe is: 0 under the line, 1 past the first line, 2 past the second. */
export type SwipeReach = 0 | 1 | 2;

/** Which lines a side has: the first (short) action and an optional second (long) one. */
export interface SwipeReaches {
	leading: {short: boolean; long: boolean};
	trailing: {short: boolean; long: boolean};
}

/** Where the second line sits: always past the first, and never off the row. */
export function longCommitDistance(
	width: number,
	longRatio = DEFAULT_LONG_RATIO,
	ratio = DEFAULT_COMMIT_RATIO,
): number {
	return Math.max(commitDistance(width, ratio) + 48, Math.min(width - 24, width * longRatio));
}

/**
 * Which action a swipe at `offset` would run. Each line keeps the same drift-back
 * slack as `isArmed`, so pulling back below a line (by more than the slack)
 * drops to the one before it. A side without a first action arms only at the second.
 */
export function swipeReach(
	offset: number,
	width: number,
	reaches: SwipeReaches,
	previous: SwipeReach,
	ratio = DEFAULT_COMMIT_RATIO,
	longRatio = DEFAULT_LONG_RATIO,
): SwipeReach {
	if (offset === 0) return 0;
	const side = offset > 0 ? reaches.leading : reaches.trailing;
	const distance = Math.abs(offset);
	if (side.long && distance >= longCommitDistance(width, longRatio, ratio) - (previous === 2 ? DISARM_SLACK_PX : 0)) {
		return 2;
	}
	if (side.short && distance >= commitDistance(width, ratio) - (previous >= 1 ? DISARM_SLACK_PX : 0)) return 1;
	return 0;
}

/** 0 at rest, 1 at the commit line; drives the icon's growth. */
export function swipeProgress(offset: number, width: number, ratio = DEFAULT_COMMIT_RATIO): number {
	return Math.min(1, Math.abs(offset) / commitDistance(width, ratio));
}

/** Centre of the icon, measured from the screen edge the row is uncovering. */
export function iconCentre(offset: number): number {
	return Math.max(ICON_EDGE_PX, Math.abs(offset) - ICON_TRAIL_PX);
}

export type ReleaseOutcome = 'commit' | 'tray' | 'cancel';

/**
 * What letting go does. `velocity` is px/ms, positive to the right. Armed
 * commits unless the finger was flung back towards rest. Short of the line a
 * side with a tray rests open on it; without one, a fast outward flick
 * commits from a shorter pull and anything else springs back.
 */
export function releaseOutcome(
	offset: number,
	velocity: number,
	sides: SwipeSides,
	armed: boolean,
	trays: SwipeTrays = NO_TRAYS,
): ReleaseOutcome {
	if (offset === 0) return 'cancel';
	const direction = Math.sign(offset);
	const full = direction > 0 ? sides.leading : sides.trailing;
	const tray = direction > 0 ? trays.leading : trays.trailing;
	const distance = Math.abs(offset);
	const outward = velocity * direction;
	if (full && armed) return outward <= -FLICK_VELOCITY ? 'cancel' : 'commit';
	if (outward <= -FLICK_VELOCITY) return 'cancel';
	if (full && outward >= FLICK_VELOCITY && distance >= Math.max(FLICK_MIN_PX, tray)) return 'commit';
	if (tray > 0 && distance >= TRAY_OPEN_MIN_PX) return 'tray';
	return 'cancel';
}
